import React, { useContext } from "react";
import { AppContext } from "../context/app.context";

/**
 * Time Machine #114 Phase 4.5 — subtle sepia tint overlay rendered inside a
 * schedule grid wrapper while time-traveling.
 *
 * Goal: visually distinguish time-travel grids from live grids — both for
 * the user actively scrubbing AND for screenshots that get pasted into
 * helpdesk threads. Even with the banner cropped out of a screenshot, the
 * tint signals "this isn't live data."
 *
 * Mechanics:
 *   - Absolutely positioned overlay covering the parent grid area
 *   - Very low opacity sepia tint (~5-8%) — visible enough to register,
 *     not enough to interfere with reading cell content
 *   - pointer-events: none so the user can still scroll/click the grid
 *     behind the overlay
 *   - Renders nothing when not time-traveling (zero overhead in live mode)
 *   - Works in BOTH light and dark mode because the tint is a warm
 *     translucent layer applied OVER whatever the underlying grid colors
 *     produce
 *
 * Drop-in usage: place inside a `position: relative` parent (the same
 * wrappers GridSpinner uses). Both can coexist — GridSpinner z-50, this
 * z-40 — so the spinner is on top during loads, but tint is always present.
 */
const TimeTravelTint = () => {
  const { isTimeTraveling } = useContext(AppContext);
  if (!isTimeTraveling) return null;
  // z-index 40 sits ABOVE the schedule grid's sticky header (z-30, bumped from
  // z-10 in #116 to put the header above crew rows' sticky-left cells and
  // above UserBubbleStack z-20) and BELOW GridSpinner (z-50). Without
  // sitting above the header, the header's opaque bg-header completely
  // covered the tint in the header row, leaving most of the visible area
  // un-tinted from the user's perspective.
  //
  // Alpha tuning history:
  //   0.07 → invisible to users in dev validation 2026-05-08
  //   0.20 → barely visible
  //   0.35 → current value; clearly distinct from live in both light + dark
  //          mode; reads as a definite sepia wash without obscuring content.
  // (We avoid CSS `filter: sepia(...)` on the wrapper because position:sticky
  //  inside a filtered ancestor has browser quirks — risk of regressing the
  //  schedule grid's sticky header + sticky-left crew column.)
  return (
    <div
      data-testid="time-travel-tint"
      aria-hidden="true"
      className="absolute inset-0 pointer-events-none"
      style={{
        backgroundColor: "rgba(112, 66, 20, 0.35)", // sepia brown, 35% alpha
        zIndex: 40, // above sticky header z-30 (#116), below GridSpinner z-50
      }}
    />
  );
};

export default TimeTravelTint;
